import React, { useEffect } from "react"
import PubSub from "pubsub-js"
import PlatformAuth from "aldoo-ra/PlatformAuth"
import Button from "aldoo-ra/Button"
import useT from "../hooks/useT"
import clientRedirect from "../utils/clientRedirect"
import { langFromPath, stripLang } from "../utils/contentRoutes"
import useSearchParams from "../hooks/useSearchParams"
import { vendorOrgsOf } from "../../Vendor/useVendorOrgs"
import { usePageTitle } from "../hooks/usePageTitle"

// Pages a merchant may still be sent to after signing in, instead of the portal.
const KEEPS_ITS_WAY = /^\/(join-org|vendor)(\/|\?|$)/

/**
 * /sign-in, /login, /sign-up — opens the aldoo-ra sign-in dialog (magic
 * link / providers) and sends the user to ?redirectTo= afterwards.
 */
export default function SignInPage() {
  const t = useT()
  const params = useSearchParams()
  const [auth] = PlatformAuth.useAuth()
  const asked = params.get("redirectTo")
  usePageTitle(t`Sign In`)

  // Where somebody lands depends on who they turn out to be, which is not
  // known until they have signed in — so this page decides, and the dialog is
  // given nowhere to go (its Continue button would otherwise overrule it).
  const open = () => PubSub.publish("signin", {})

  useEffect(() => {
    if (auth) {
      const lang = langFromPath(window.location.pathname)
      const prefix = lang ? `/${lang}` : ""
      // Somebody who sells here signs in to work: the portal comes first, even
      // over the page they were on their way to. Only an invitation to join an
      // organization, or a page of the portal itself, is let through.
      vendorOrgsOf(auth._id).then((orgs) => {
        if (asked && (!orgs.length || KEEPS_ITS_WAY.test(stripLang(asked)))) return clientRedirect(asked)
        clientRedirect(orgs.length ? `${prefix}/vendor` : prefix || "/")
      })
      return
    }
    const timer = setTimeout(open, 300)
    return () => clearTimeout(timer)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [auth])

  return (
    <div className="w-full flex flex-col items-center px-4 py-24 gap-6 text-center">
      <img src="/static/p-images/aldoo-small--150x150.webp" alt="Aldoo" width={90} height={90} />
      <h1 className="m-0 text-[32px] md:text-[44px] font-bold">{t`Sign In`}</h1>
      <p className="m-0 max-w-[480px] text-text_secondary text-[16px] leading-relaxed">
        {t`Sign in or create your Aldoo account with your email.`}
      </p>
      <Button variant="primary_l" onClick={open}>
        {t`Sign In`}
      </Button>
      <p className="m-0 text-[14px] text-text_secondary" data-testid="signin-consent">
        {t`By signing in you agree with our`}{" "}
        <Button variant="link" href="/terms-of-use">{t`Term of Use`}</Button>{" "}
        {t`and`}{" "}
        <Button variant="link" href="/privacy-policy">{t`Privacy Policy`}</Button>
      </p>
    </div>
  )
}
